import { NgIf, AsyncPipe } from '@angular/common';
import { Component, Inject, OnDestroy } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatCard, MatCardHeader, MatCardContent } from '@angular/material/card';
import { MatDivider } from '@angular/material/divider';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { Router, RouterLink } from '@angular/router';
import { AuthorizationSelectors } from '@next/core-lib/authorization';
import { Cart, CartLifeCycle, OrderTypes } from '@next/core-lib/cart';
import { AgrLocationSummaryModule, AgrNotificationEmptyModule } from '@next/core-lib/components';
import { EnvironmentInjector } from '@next/core-lib/environment';
import { AgrI18nSharedModule } from '@next/core-lib/i18n';
import { AgrPipesModule } from '@next/core-lib/pipes';
import { RouterSelectors } from '@next/core-lib/router-store';
import { SessionSelectors } from '@next/core-lib/session';
import { SelectedCustomer, SelectedLocation, UserSelectors } from '@next/core-lib/user';
import { select, Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { map, skipWhile, switchMap, takeUntil } from 'rxjs/operators';
import { Environment } from 'src/environments/environment.model';

import { AppState } from '../../models/app.models';
import * as AppSelectors from '../../store/selectors';

import { CartComponent } from '../cart/cart.component';
import { TotalOrderedWeightComponent } from '../cart-confirmation/total-ordered-weight/total-ordered-weight.component';

@Component({
  selector: 'app-cart-thank-you',
  templateUrl: './cart-thank-you.component.html',
  styleUrls: ['./cart-thank-you.component.scss'],
  imports: [
    NgIf,
    MatButton,
    RouterLink,
    MatIcon,
    MatProgressSpinner,
    MatDivider,
    MatCard,
    MatCardHeader,
    MatCardContent,
    AgrLocationSummaryModule,
    CartComponent,
    TotalOrderedWeightComponent,
    AgrNotificationEmptyModule,
    AsyncPipe,
    AgrI18nSharedModule,
    AgrPipesModule,
  ],
})
export class CartThankYouComponent implements OnDestroy {
  public readonly destroy$: Subject<void> = new Subject<void>();
  public readonly returnableOrderType = OrderTypes.returnableOrder;
  public readonly pickUpOrderType = OrderTypes.pickUpOrder;
  public location$: Observable<SelectedLocation> = this.store.select(UserSelectors.selectLocation);
  public customer$: Observable<SelectedCustomer> = this.store.select(UserSelectors.selectCustomer);

  public invoiceUrl$: Observable<string> = this.store.select(
    SessionSelectors.selectUrlWithSession({
      url: this.env.MY_OFFICE_URL + '/invoice',
    }),
  );

  public cartId$: Observable<number> = this.store.select(RouterSelectors.getRouteParam('cartId')).pipe(map((cartId: string) => parseInt(cartId, 10)));

  public cart$: Observable<Cart> = this.cartId$.pipe(
    switchMap((cartId) => this.store.select(AppSelectors.selectCartWithHasRemarksValidationsSilosOrSupplements(cartId))),
    skipWhile((val) => !val),
    takeUntil(this.destroy$),
  );

  public isBeingProcessed$: Observable<boolean> = this.cart$.pipe(
    skipWhile((val) => !val),
    select((cart) => [CartLifeCycle.submitted, CartLifeCycle.readyForSubmission].includes(cart?.status)),
  );

  public isAtLeastAdvisor$: Observable<boolean> = this.store.select(AuthorizationSelectors.hasAtLeastAdvisorRole);

  constructor(
    private readonly store: Store<AppState>,
    private readonly router: Router,
    @Inject(EnvironmentInjector) private readonly env: Environment,
  ) {}

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  navigateToOverview(changeCustomer: boolean): void {
    if (changeCustomer) {
      window.location.replace(this.env.SHOP_URL);
    } else {
      this.router.navigate(['']);
    }
  }
}
